import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/Loader";
import { LogoMark } from "@/components/LogoMark";
import { PlainField, BilingualField, StringList, ObjectList, FileUploadField } from "@/components/admin/fields";
import { LogOut, Save, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { v: "draft", label: "Draft — visible to admins only" },
  { v: "active", label: "Active — travelers with the link can access" },
  { v: "archived", label: "Archived — shows the concluded page" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Loader />;

  const settings = data.settings || {};
  const setSettings = (patch) => setData({ ...data, settings: { ...settings, ...patch } });
  const setKey = (key, val) => setData({ ...data, [key]: val });
  const setNested = (key, patch) => setData({ ...data, [key]: { ...(data[key] || {}), ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...data };
      delete clean._id;
      delete clean.updated_at;
      await api.put("/content", { content: clean });
      toast.success("Changes saved — travelers see them instantly");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    ["home", "Home & Updates"],
    ["travel", "Travel"],
    ["hotels", "Hotels"],
    ["itinerary", "Itinerary"],
    ["umrah", "Umrah Guide"],
    ["documents", "Documents"],
    ["maps", "Maps"],
    ["food", "Food"],
    ["faq", "FAQ"],
    ["support", "Support"],
  ];

  const travel = data.travel || {};
  const umrah = data.umrahGuide || {};
  const documents = data.documents || {};
  const maps = data.maps || {};
  const food = data.food || {};
  const support = data.support || {};

  return (
    <div className="min-h-[100svh] bg-[#F1F5F9]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[#0A192F]/10 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8" />
            <div className="hidden sm:block">
              <p className="font-display text-sm font-bold text-[#0A192F]">Journey Admin</p>
              <p className="text-xs text-[#64748B]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 text-sm text-[#0A192F] sm:inline-flex">
              View journey <ExternalLink size={14} />
            </a>
            <Button onClick={save} disabled={saving} data-testid="admin-save-btn" className="gap-1.5 bg-[#D4AF37] text-[#0A1128] hover:bg-[#BCA054]">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </Button>
            <Button onClick={logout} variant="outline" size="icon" data-testid="admin-logout-btn" aria-label="Log out">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <Tabs defaultValue="home">
          <div className="mb-5 overflow-x-auto hide-scrollbar">
            <TabsList className="inline-flex w-max bg-white">
              {tabs.map(([v, label]) => (
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* HOME & UPDATES */}
          <TabsContent value="home" className="space-y-8">
            <Card title="Trip Status">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#0A192F]">Journey visibility</Label>
                <select
                  value={settings.status || "active"}
                  onChange={(e) => setSettings({ status: e.target.value })}
                  data-testid="admin-status-select"
                  className="h-11 w-full rounded-lg border border-input bg-white px-3 text-sm"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-[#64748B]">
                  Draft & Archived journeys are hidden from travelers. You (logged in) always see a preview.
                </p>
              </div>
              <BilingualField label="Draft page message" value={settings.draftMessage} onChange={(v) => setSettings({ draftMessage: v })} textarea />
            </Card>

            <Card title="Hero & Welcome">
              <BilingualField label="Hero title" value={settings.heroTitle} onChange={(v) => setSettings({ heroTitle: v })} />
              <PlainField label="Arabic greeting (shown as-is)" value={settings.greeting} onChange={(v) => setSettings({ greeting: v })} />
              <BilingualField label="Welcome message" value={settings.welcomeBody} onChange={(v) => setSettings({ welcomeBody: v })} textarea />
              <BilingualField label="Trip dates (display text)" value={settings.tripDates} onChange={(v) => setSettings({ tripDates: v })} />
              <div className="grid grid-cols-2 gap-3">
                <PlainField label="Trip start" type="date" value={settings.tripStartDate} onChange={(v) => setSettings({ tripStartDate: v })} />
                <PlainField label="Trip end" type="date" value={settings.tripEndDate} onChange={(v) => setSettings({ tripEndDate: v })} />
              </div>
              <BilingualField label="Destination" value={settings.destination} onChange={(v) => setSettings({ destination: v })} />
              <BilingualField label="Button label" value={settings.ctaLabel} onChange={(v) => setSettings({ ctaLabel: v })} />
              <FileUploadField label="Hero background image" value={settings.heroImage} onChange={(v) => setSettings({ heroImage: v })} />
            </Card>

            <Card title="Latest Updates (Announcements)">
              <ObjectList
                value={data.announcements}
                onChange={(v) => setKey("announcements", v)}
                template={() => ({ date: new Date().toISOString().slice(0, 10), priority: "new", title: { en: "", fr: "" }, body: { en: "", fr: "" } })}
                itemTitle={(it) => it.title?.en || "Announcement"}
                renderItem={(it, update) => (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <PlainField label="Date (YYYY-MM-DD)" value={it.date} onChange={(v) => update({ date: v })} />
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#0A192F]">Priority label</label>
                        <select
                          value={it.priority || "new"}
                          onChange={(e) => update({ priority: e.target.value })}
                          className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                        >
                          <option value="new">New</option>
                          <option value="reminder">Reminder</option>
                          <option value="action">Action Required</option>
                          <option value="important">Important</option>
                        </select>
                      </div>
                    </div>
                    <BilingualField label="Title" value={it.title} onChange={(v) => update({ title: v })} />
                    <BilingualField label="Body" value={it.body} onChange={(v) => update({ body: v })} textarea />
                  </>
                )}
              />
            </Card>
            <ChangePasswordCard />
          </TabsContent>

          {/* TRAVEL */}
          <TabsContent value="travel" className="space-y-8">
            <Card title="Travel Details">
              <BilingualField label="Intro" value={travel.intro} onChange={(v) => setNested("travel", { intro: v })} textarea />
              <ObjectList
                label="Flights"
                value={travel.flights || []}
                onChange={(v) => setNested("travel", { flights: v })}
                template={() => ({ label: {}, airline: {}, flightNumber: "", from: {}, to: {}, departure: {}, arrival: {}, terminal: {}, baggage: {} })}
                itemTitle={(it) => it.label?.en || "Flight"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Label" value={it.label} onChange={(v) => update({ label: v })} />
                    <BilingualField label="Airline" value={it.airline} onChange={(v) => update({ airline: v })} />
                    <PlainField label="Flight number" value={it.flightNumber} onChange={(v) => update({ flightNumber: v })} />
                    <BilingualField label="From" value={it.from} onChange={(v) => update({ from: v })} />
                    <BilingualField label="To" value={it.to} onChange={(v) => update({ to: v })} />
                    <BilingualField label="Departure" value={it.departure} onChange={(v) => update({ departure: v })} />
                    <BilingualField label="Arrival" value={it.arrival} onChange={(v) => update({ arrival: v })} />
                    <BilingualField label="Terminal" value={it.terminal} onChange={(v) => update({ terminal: v })} />
                    <BilingualField label="Baggage" value={it.baggage} onChange={(v) => update({ baggage: v })} />
                  </>
                )}
              />
              <BilingualField label="Airport arrival instructions" value={travel.arrivalInstructions} onChange={(v) => setNested("travel", { arrivalInstructions: v })} textarea />
            </Card>
          </TabsContent>

          {/* HOTELS */}
          <TabsContent value="hotels" className="space-y-8">
            <Card title="Hotels">
              <ObjectList
                value={data.hotels || []}
                onChange={(v) => setKey("hotels", v)}
                template={() => ({ city: {}, name: {}, image: "", address: {}, mapsUrl: "", phone: "", walkingDistance: {}, checkIn: {}, amenities: [] })}
                itemTitle={(it) => it.name?.en || it.city?.en || "Hotel"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="City" value={it.city} onChange={(v) => update({ city: v })} />
                    <BilingualField label="Hotel name" value={it.name} onChange={(v) => update({ name: v })} />
                    <FileUploadField label="Hotel photo" value={it.image} onChange={(v) => update({ image: v })} />
                    <BilingualField label="Address" value={it.address} onChange={(v) => update({ address: v })} />
                    <div className="grid grid-cols-2 gap-3">
                      <PlainField label="Google Maps URL" value={it.mapsUrl} onChange={(v) => update({ mapsUrl: v })} />
                      <PlainField label="Phone" value={it.phone} onChange={(v) => update({ phone: v })} />
                    </div>
                    <BilingualField label="Walking distance to Haram" value={it.walkingDistance} onChange={(v) => update({ walkingDistance: v })} />
                    <BilingualField label="Check-in info" value={it.checkIn} onChange={(v) => update({ checkIn: v })} />
                    <StringList label="Nearby amenities" value={it.amenities} onChange={(v) => update({ amenities: v })} />
                  </>
                )}
              />
            </Card>
          </TabsContent>

          {/* ITINERARY */}
          <TabsContent value="itinerary" className="space-y-8">
            <Card title="Daily Itinerary">
              <ObjectList
                value={data.itinerary || []}
                onChange={(v) => setKey("itinerary", v)}
                template={() => ({ day: {}, title: {}, items: [], dayFrom: 1, dayTo: 1 })}
                itemTitle={(it) => it.title?.en || "Day"}
                renderItem={(it, update) => (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <PlainField label="Trip day from" type="number" value={it.dayFrom} onChange={(v) => update({ dayFrom: v === "" ? "" : Number(v) })} />
                      <PlainField label="Trip day to" type="number" value={it.dayTo} onChange={(v) => update({ dayTo: v === "" ? "" : Number(v) })} />
                    </div>
                    <BilingualField label="Day label" value={it.day} onChange={(v) => update({ day: v })} />
                    <BilingualField label="Title" value={it.title} onChange={(v) => update({ title: v })} />
                    <StringList label="Schedule items" value={it.items} onChange={(v) => update({ items: v })} />
                  </>
                )}
              />
            </Card>
          </TabsContent>

          {/* UMRAH */}
          <TabsContent value="umrah" className="space-y-8">
            <Card title="Umrah Guide">
              <BilingualField label="Intro" value={umrah.intro} onChange={(v) => setNested("umrahGuide", { intro: v })} textarea />
              <ObjectList
                label="Steps"
                value={umrah.steps || []}
                onChange={(v) => setNested("umrahGuide", { steps: v })}
                template={() => ({ title: {}, body: {} })}
                itemTitle={(it) => it.title?.en || "Step"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Title" value={it.title} onChange={(v) => update({ title: v })} />
                    <BilingualField label="Description" value={it.body} onChange={(v) => update({ body: v })} textarea />
                  </>
                )}
              />
              <StringList label="Ihram checklist" value={umrah.ihramChecklist} onChange={(v) => setNested("umrahGuide", { ihramChecklist: v })} />
              <StringList label="Do's" value={umrah.dos} onChange={(v) => setNested("umrahGuide", { dos: v })} />
              <StringList label="Don'ts" value={umrah.donts} onChange={(v) => setNested("umrahGuide", { donts: v })} />
              <BilingualField label="Du'a guide" value={umrah.duaGuide} onChange={(v) => setNested("umrahGuide", { duaGuide: v })} textarea />
            </Card>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents" className="space-y-8">
            <Card title="Documents">
              <BilingualField label="Intro" value={documents.intro} onChange={(v) => setNested("documents", { intro: v })} textarea />
              <ObjectList
                label="Document items"
                value={documents.items || []}
                onChange={(v) => setNested("documents", { items: v })}
                template={() => ({ title: {}, status: {}, fileUrl: "" })}
                itemTitle={(it) => it.title?.en || "Document"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Title" value={it.title} onChange={(v) => update({ title: v })} />
                    <BilingualField label="Status" value={it.status} onChange={(v) => update({ status: v })} />
                    <FileUploadField label="Attached file (PDF/image)" value={it.fileUrl} onChange={(v) => update({ fileUrl: v })} />
                  </>
                )}
              />
              <StringList label="Important reminders" value={documents.reminders} onChange={(v) => setNested("documents", { reminders: v })} />
            </Card>
          </TabsContent>

          {/* MAPS */}
          <TabsContent value="maps" className="space-y-8">
            <Card title="Maps & Navigation">
              <BilingualField label="Intro" value={maps.intro} onChange={(v) => setNested("maps", { intro: v })} textarea />
              <ObjectList
                label="Places"
                value={maps.places || []}
                onChange={(v) => setNested("maps", { places: v })}
                template={() => ({ title: {}, note: {}, mapsUrl: "" })}
                itemTitle={(it) => it.title?.en || "Place"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Title" value={it.title} onChange={(v) => update({ title: v })} />
                    <BilingualField label="Note" value={it.note} onChange={(v) => update({ note: v })} />
                    <PlainField label="Google Maps URL" value={it.mapsUrl} onChange={(v) => update({ mapsUrl: v })} />
                  </>
                )}
              />
            </Card>
          </TabsContent>

          {/* FOOD */}
          <TabsContent value="food" className="space-y-8">
            <Card title="Food & Shopping">
              <BilingualField label="Intro" value={food.intro} onChange={(v) => setNested("food", { intro: v })} textarea />
              <ObjectList
                label="Categories"
                value={food.categories || []}
                onChange={(v) => setNested("food", { categories: v })}
                template={() => ({ title: {}, items: [] })}
                itemTitle={(it) => it.title?.en || "Category"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Category title" value={it.title} onChange={(v) => update({ title: v })} />
                    <StringList label="Items" value={it.items} onChange={(v) => update({ items: v })} />
                  </>
                )}
              />
            </Card>
          </TabsContent>

          {/* FAQ */}
          <TabsContent value="faq" className="space-y-8">
            <Card title="Frequently Asked Questions">
              <ObjectList
                value={data.faqs || []}
                onChange={(v) => setKey("faqs", v)}
                template={() => ({ q: {}, a: {} })}
                itemTitle={(it) => it.q?.en || "Question"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Question" value={it.q} onChange={(v) => update({ q: v })} />
                    <BilingualField label="Answer" value={it.a} onChange={(v) => update({ a: v })} textarea />
                  </>
                )}
              />
            </Card>
          </TabsContent>

          {/* SUPPORT */}
          <TabsContent value="support" className="space-y-8">
            <Card title="Support & Emergency">
              <BilingualField label="Intro" value={support.intro} onChange={(v) => setNested("support", { intro: v })} textarea />
              <ObjectList
                label="Key contacts"
                value={support.contacts || []}
                onChange={(v) => setNested("support", { contacts: v })}
                template={() => ({ role: {}, name: {}, phone: "", whatsapp: "" })}
                itemTitle={(it) => it.role?.en || "Contact"}
                renderItem={(it, update) => (
                  <>
                    <BilingualField label="Role" value={it.role} onChange={(v) => update({ role: v })} />
                    <BilingualField label="Name" value={it.name} onChange={(v) => update({ name: v })} />
                    <div className="grid grid-cols-2 gap-3">
                      <PlainField label="Phone" value={it.phone} onChange={(v) => update({ phone: v })} />
                      <PlainField label="WhatsApp number" value={it.whatsapp} onChange={(v) => update({ whatsapp: v })} />
                    </div>
                  </>
                )}
              />
              <StringList label="Emergency numbers" value={support.emergency} onChange={(v) => setNested("support", { emergency: v })} />
              <BilingualField label="Lost traveler instructions" value={support.lostTraveler} onChange={(v) => setNested("support", { lostTraveler: v })} textarea />
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Card = ({ title, children }) => (
  <section className="rounded-2xl border border-[#0A192F]/10 bg-white p-5 sm:p-6">
    <h2 className="mb-5 font-display text-2xl font-bold text-[#0A192F]">{title}</h2>
    <div className="space-y-5">{children}</div>
  </section>
);

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#0A192F]/10 bg-white p-5 sm:p-6">
      <h2 className="mb-1.5 flex items-center gap-2 font-display text-2xl font-bold text-[#0A192F]">
        <ShieldCheck size={22} className="text-[#D4AF37]" /> Security
      </h2>
      <p className="mb-5 text-sm text-[#64748B]">Set your own secure password. Use at least 8 characters.</p>
      <form onSubmit={submit} className="max-w-md space-y-4" data-testid="change-password-form">
        <div className="space-y-1.5">
          <Label>Current password</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" required />
        </div>
        <div className="space-y-1.5">
          <Label>New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" required />
        </div>
        <div className="space-y-1.5">
          <Label>Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" required />
        </div>
        <Button type="submit" disabled={busy} data-testid="cp-submit" className="gap-1.5 bg-[#0A192F] hover:bg-[#112240]">
          {busy && <Loader2 size={15} className="animate-spin" />} Update password
        </Button>
      </form>
    </section>
  );
};

